import React, { useState, useEffect } from 'react';

const GolfPreferences = ({ user, onSave }) => {
  const [preferences, setPreferences] = useState({
    preferredHand: '',
    clubBrand: '',
    ballBrand: '',
    focusAreas: [],
    launchMonitors: [],
    dataFrequency: '',
    notificationPreferences: {
      practiceReminders: false,
      newInsights: false,
      productUpdates: false,
      communityEvents: false
    },
    displayUnits: 'yards',
    dataPrivacy: 'private'
  });

  const focusAreaOptions = [
    'Driving Distance', 'Driving Accuracy', 'Iron Play', 
    'Wedge Play', 'Putting', 'Course Management'
  ];

  const launchMonitorOptions = [
    'Trackman', 'GCQuad', 'Flightscope', 'SkyTrak', 
    'Garmin R10', 'Rapsodo', 'Full Swing', 'Other'
  ];

  useEffect(() => {
    // In a real app, you would fetch the user's preferences from your backend
    // For now, we'll just use placeholder data
    setPreferences({
      preferredHand: 'right',
      clubBrand: '',
      ballBrand: '',
      focusAreas: ['Driving Distance', 'Putting'],
      launchMonitors: ['Trackman'],
      dataFrequency: 'weekly',
      notificationPreferences: {
        practiceReminders: true,
        newInsights: true,
        productUpdates: false,
        communityEvents: false
      },
      displayUnits: 'yards',
      dataPrivacy: 'private'
    });
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('notification-')) {
      const notificationType = name.replace('notification-', '');
      setPreferences(prev => ({
        ...prev,
        notificationPreferences: {
          ...prev.notificationPreferences,
          [notificationType]: checked
        }
      }));
    } else if (type === 'checkbox') {
      const array = name === 'focusAreas' ? [...preferences.focusAreas] : [...preferences.launchMonitors];
      if (checked) {
        array.push(value);
      } else {
        const index = array.indexOf(value);
        if (index !== -1) {
          array.splice(index, 1);
        }
      }
      setPreferences(prev => ({
        ...prev,
        [name]: array
      }));
    } else {
      setPreferences(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // In a real app, you would send this data to your backend
      console.log('Saving preferences:', preferences);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSave(true, 'Golf preferences updated successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      onSave(false, 'Failed to update preferences. Please try again.');
    }
  };

  return (
    <div className="profile-section">
      <h2>Golf Preferences</h2>
      <p className="section-description">
        Customize your experience and help us provide more relevant insights for your game.
      </p>
      
      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="preferredHand">Preferred Hand</label>
            <select
              id="preferredHand"
              name="preferredHand"
              value={preferences.preferredHand}
              onChange={handleChange}
            >
              <option value="">Select hand</option>
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="clubBrand">Preferred Club Brand</label>
            <input
              type="text"
              id="clubBrand"
              name="clubBrand"
              value={preferences.clubBrand}
              onChange={handleChange}
              placeholder="e.g., Titleist, TaylorMade, Callaway"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="ballBrand">Preferred Golf Ball</label>
          <input
            type="text"
            id="ballBrand"
            name="ballBrand"
            value={preferences.ballBrand}
            onChange={handleChange}
            placeholder="e.g., Pro V1, TP5, Chrome Soft"
          />
        </div>
        
        <div className="form-group">
          <label>Areas You Want to Improve</label>
          <div className="checkbox-group">
            {focusAreaOptions.map(area => (
              <label key={area} className="checkbox-label">
                <input
                  type="checkbox"
                  name="focusAreas"
                  value={area}
                  checked={preferences.focusAreas.includes(area)}
                  onChange={handleChange}
                />
                {area}
              </label>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label>Launch Monitors You Use</label>
          <div className="checkbox-group">
            {launchMonitorOptions.map(monitor => (
              <label key={monitor} className="checkbox-label">
                <input
                  type="checkbox"
                  name="launchMonitors"
                  value={monitor}
                  checked={preferences.launchMonitors.includes(monitor)}
                  onChange={handleChange}
                />
                {monitor}
              </label>
            ))}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="dataFrequency">How often do you collect launch monitor data?</label>
          <select
            id="dataFrequency"
            name="dataFrequency"
            value={preferences.dataFrequency}
            onChange={handleChange}
          >
            <option value="">Select frequency</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="rarely">Rarely</option>
          </select>
        </div>
        
        <h3>Display Preferences</h3>
        
        <div className="form-group">
          <label htmlFor="displayUnits">Preferred Measurement Units</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="displayUnits"
                value="yards"
                checked={preferences.displayUnits === 'yards'}
                onChange={handleChange}
              />
              Yards
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="displayUnits"
                value="meters"
                checked={preferences.displayUnits === 'meters'}
                onChange={handleChange}
              />
              Meters
            </label>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="dataPrivacy">Data Privacy</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="dataPrivacy"
                value="private"
                checked={preferences.dataPrivacy === 'private'}
                onChange={handleChange}
              />
              Private (Only visible to you)
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="dataPrivacy"
                value="friends"
                checked={preferences.dataPrivacy === 'friends'}
                onChange={handleChange}
              />
              Friends (Visible to connections)
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="dataPrivacy"
                value="public"
                checked={preferences.dataPrivacy === 'public'}
                onChange={handleChange}
              />
              Public (Visible to all RangeDat users)
            </label>
          </div>
        </div>
        
        <h3>Notification Preferences</h3>
        
        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="notification-practiceReminders"
                checked={preferences.notificationPreferences.practiceReminders}
                onChange={handleChange}
              />
              Practice Reminders
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="notification-newInsights"
                checked={preferences.notificationPreferences.newInsights}
                onChange={handleChange}
              />
              New Insights Available
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="notification-productUpdates"
                checked={preferences.notificationPreferences.productUpdates}
                onChange={handleChange}
              />
              Product Updates
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="notification-communityEvents"
                checked={preferences.notificationPreferences.communityEvents}
                onChange={handleChange}
              />
              Community Events
            </label>
          </div>
        </div>
        
        <div className="form-actions">
          <button type="submit" className="btn-primary">Save Preferences</button>
        </div>
      </form>
    </div>
  );
};

export default GolfPreferences;
